import React, { Suspense, lazy, useCallback, useMemo, useState } from 'react';
import { Alert, Box, Button, IconButton, Link, Spinner, Stack, Text } from '@chakra-ui/react';
import { LuEye, LuEyeOff, LuX } from 'react-icons/lu';
import { useColorModeValue } from '../../components/ui/color-mode';
import { Tooltip as UiTooltip } from '../../components/ui/tooltip';

const NOTABLE_DATE_SNAP_PX = 28;
const NOTABLE_DATE_SNAP_MIN_MS = 3 * 24 * 60 * 60 * 1000;
const NOTABLE_DATE_SNAP_DEFAULT_PLOT_WIDTH_PX = 640;

export function getNotableDateSnapThresholdMs(
  xDomain,
  {
    plotWidthPx = NOTABLE_DATE_SNAP_DEFAULT_PLOT_WIDTH_PX,
    snapPx = NOTABLE_DATE_SNAP_PX,
  } = {},
) {
  const [domainStart, domainEnd] = Array.isArray(xDomain) ? xDomain : [];
  if (!Number.isFinite(domainStart) || !Number.isFinite(domainEnd) || domainEnd <= domainStart) {
    return 14 * 24 * 60 * 60 * 1000;
  }
  if (!Number.isFinite(plotWidthPx) || plotWidthPx <= 0) {
    return NOTABLE_DATE_SNAP_MIN_MS;
  }
  return Math.max(((domainEnd - domainStart) / plotWidthPx) * snapPx, NOTABLE_DATE_SNAP_MIN_MS);
}

export function findNearestNotableDateKey(cursorTimestamp, markers, thresholdMs) {
  if (!Number.isFinite(cursorTimestamp) || !Array.isArray(markers) || markers.length === 0) {
    return null;
  }
  if (!Number.isFinite(thresholdMs) || thresholdMs < 0) {
    return null;
  }

  let nearestKey = null;
  let nearestDistance = Infinity;
  for (const marker of markers) {
    const timestamp = Number(marker?.timestamp);
    if (!Number.isFinite(timestamp)) continue;
    const distance = Math.abs(timestamp - cursorTimestamp);
    if (distance < nearestDistance) {
      nearestDistance = distance;
      nearestKey = marker.key;
    }
  }

  return nearestDistance <= thresholdMs ? nearestKey : null;
}

function getNotableDateLabelPosition(timestamp, xDomain) {
  const [domainStart, domainEnd] = Array.isArray(xDomain) ? xDomain : [];
  if (!Number.isFinite(domainStart) || !Number.isFinite(domainEnd) || domainEnd <= domainStart) {
    return 'insideTopLeft';
  }

  const rightSideThreshold = domainStart + ((domainEnd - domainStart) * 0.75);
  return timestamp >= rightSideThreshold ? 'insideTopRight' : 'insideTopLeft';
}

const StockPriceHistoryRechartsShell = lazy(() =>
  import('recharts').then(({
    ResponsiveContainer,
    LineChart,
    Line,
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip,
    ReferenceLine,
  }) => ({
    default: function StockPriceHistoryRechartsShell({
      chartData,
      gridStroke,
      axisColor,
      formatPriceTick,
      formatDateTick,
      tooltipRenderer,
      referenceDate,
      referenceLabel,
      referenceTimestamp,
      notableDateMarkers,
      hiddenNotableDateKeys,
      hoveredNotableDateKey,
      onNotableDateHover,
      xDomain,
    }) {
      const snapThresholdMs = getNotableDateSnapThresholdMs(xDomain);
      const visibleNotableDateMarkers = (notableDateMarkers || []).filter(
        (marker) => !hiddenNotableDateKeys?.has?.(marker.key),
      );

      const handleChartMouseMove = (state) => {
        if (!visibleNotableDateMarkers.length) return;
        const cursorTimestamp = Number(state?.activeLabel);
        const nextKey = findNearestNotableDateKey(
          cursorTimestamp,
          visibleNotableDateMarkers,
          snapThresholdMs,
        );
        onNotableDateHover(nextKey);
      };

      return (
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={chartData}
            margin={{ top: 8, right: 8, left: 0, bottom: 8 }}
            onMouseMove={handleChartMouseMove}
            onMouseLeave={() => onNotableDateHover(null)}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
            <XAxis
              dataKey="dateMs"
              type="number"
              scale="time"
              domain={xDomain}
              tickFormatter={formatDateTick}
              tick={{ fontSize: 12, fill: axisColor }}
              tickMargin={8}
              minTickGap={28}
            />
            <YAxis
              dataKey="price"
              tickFormatter={formatPriceTick}
              tick={{ fontSize: 12, fill: axisColor }}
              tickMargin={6}
              width={68}
              domain={['auto', 'auto']}
            />
            <Tooltip content={tooltipRenderer} />
            {referenceTimestamp ? (
              <ReferenceLine
                x={referenceTimestamp}
                stroke="#E53E3E"
                strokeWidth={2}
                strokeDasharray="4 4"
                label={{
                  value: referenceLabel || 'Snapshot',
                  position: 'insideTopRight',
                  fill: '#E53E3E',
                  fontSize: 12,
                }}
              />
            ) : null}
            {visibleNotableDateMarkers.map((marker) => (
              <ReferenceLine
                key={`hit-${marker.key}`}
                x={marker.timestamp}
                stroke="transparent"
                strokeWidth={NOTABLE_DATE_SNAP_PX * 2}
                ifOverflow="extendDomain"
                onMouseEnter={() => onNotableDateHover(marker.key)}
                onMouseLeave={() => onNotableDateHover(null)}
              />
            ))}
            {(notableDateMarkers || []).map((marker) => {
              const isHidden = Boolean(hiddenNotableDateKeys?.has?.(marker.key));
              const isHovered = !isHidden && hoveredNotableDateKey === marker.key;
              return (
                <ReferenceLine
                  key={marker.key}
                  x={marker.timestamp}
                  stroke={isHidden ? 'transparent' : '#805AD5'}
                  strokeOpacity={isHidden ? 0 : 1}
                  strokeWidth={isHidden ? 0 : (isHovered ? 3 : 1.5)}
                  strokeDasharray={isHovered ? undefined : '3 3'}
                  label={isHovered ? {
                    value: marker.event_date,
                    position: getNotableDateLabelPosition(marker.timestamp, xDomain),
                    fill: '#6B46C1',
                    fontSize: 11,
                    fontWeight: 700,
                  } : undefined}
                  aria-label={`${marker.event_date}: ${marker.description}`}
                  title={`${marker.event_date}: ${marker.description}`}
                />
              );
            })}
            <Line
              type="monotone"
              dataKey="price"
              name="Stock Price"
              stroke="#2C7A7B"
              strokeWidth={2}
              dot={false}
              connectNulls
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      );
    },
  })),
);

function toFiniteNumber(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

function parseDateMs(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) {
    return null;
  }

  const timestamp = Date.parse(`${value}T00:00:00Z`);
  return Number.isFinite(timestamp) ? timestamp : null;
}

function formatDateTick(value) {
  const timestamp = Number(value);
  if (!Number.isFinite(timestamp)) {
    return '';
  }

  return new Date(timestamp).toISOString().slice(0, 10);
}

const PRICE_TICK_WHOLE_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const PRICE_TICK_DECIMAL_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2,
});

function formatPriceTick(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return '';
  }

  if (Math.abs(numericValue) >= 1000) {
    return `$${Math.round(numericValue).toLocaleString()}`;
  }

  return (
    numericValue >= 100 ? PRICE_TICK_WHOLE_FORMATTER : PRICE_TICK_DECIMAL_FORMATTER
  ).format(numericValue);
}

function formatVolume(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return null;
  }

  return Math.round(numericValue).toLocaleString('en-US');
}

const TOOLTIP_PRICE_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function formatTooltipPrice(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return '';
  }

  return TOOLTIP_PRICE_FORMATTER.format(numericValue);
}

function getErrorMessage(error) {
  if (!error) {
    return 'Unable to load stock price history.';
  }

  if (typeof error === 'string') {
    return error;
  }

  return error.message || 'Unable to load stock price history.';
}

function getStatusCopy(priceStatus, priceMessage, redactionEnabled) {
  if (priceStatus === 'partial') {
    return {
      label: 'Partial history',
      detail: priceMessage || 'Some prices in this range are unavailable.',
      tone: 'orange',
    };
  }

  if (priceStatus === 'unavailable') {
    return {
      label: redactionEnabled ? 'Pre-snapshot history unavailable' : 'Price history unavailable',
      detail: priceMessage || 'No usable prices were found for this range.',
      tone: 'gray',
    };
  }

  return null;
}

const EMPTY_PRICE_HISTORY = [];
const EMPTY_NOTABLE_DATES = [];

function normalizeNotableDateMarker(notableDate) {
  const timestamp = parseDateMs(notableDate?.event_date);
  if (timestamp === null) {
    return null;
  }

  const description = String(notableDate?.event_description || 'Notable event').trim();
  return {
    ...notableDate,
    key: String(notableDate?.id ?? `${notableDate?.event_date || ''}:${description}`),
    timestamp,
    description,
  };
}

export default function StockPriceHistoryChart({
  prices = EMPTY_PRICE_HISTORY,
  loading = false,
  error = null,
  redactionEnabled = false,
  preferAdjustedClose = true,
  priceStatus = null,
  priceMessage = null,
  referenceDate = null,
  referenceLabel = null,
  notableDates = EMPTY_NOTABLE_DATES,
  onRemoveNotableDate = null,
}) {
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const mutedText = useColorModeValue('gray.600', 'gray.400');
  const panelBg = useColorModeValue('white', 'gray.900');
  const emptyBg = useColorModeValue('gray.50', 'gray.800');
  const gridStroke = useColorModeValue('#E2E8F0', '#2D3748');
  const axisColor = useColorModeValue('#4A5568', '#CBD5E0');
  const tooltipBg = useColorModeValue('white', 'gray.800');
  const tooltipBorder = useColorModeValue('gray.200', 'gray.700');
  const tooltipText = useColorModeValue('gray.800', 'gray.100');
  const tooltipMuted = useColorModeValue('gray.600', 'gray.400');
  const notableHoverBg = useColorModeValue('purple.50', 'whiteAlpha.100');
  const notableMutedBg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const [hiddenNotableDateKeys, setHiddenNotableDateKeys] = useState(() => new Set());
  const [hoveredNotableDateKey, setHoveredNotableDateKey] = useState(null);
  const [removingNotableDateKey, setRemovingNotableDateKey] = useState(null);

  const chartData = useMemo(() => (
    (Array.isArray(prices) ? prices : [])
      .reduce((rows, price) => {
        const dateMs = parseDateMs(price?.trading_date);
        const adjustedClose = toFiniteNumber(price?.adj_close);
        const close = toFiniteNumber(price?.close);
        const plottedPrice = preferAdjustedClose && adjustedClose !== null ? adjustedClose : close;

        if (!price?.trading_date || dateMs === null || plottedPrice === null) {
          return rows;
        }

        rows.push({
          date: price?.trading_date,
          dateMs,
          price: plottedPrice,
          close,
          adj_close: adjustedClose,
          volume: price?.volume ?? null,
        });

        return rows;
      }, [])
  ), [preferAdjustedClose, prices]);
  const statusCopy = getStatusCopy(priceStatus, priceMessage, redactionEnabled);
  const firstChartDate = chartData[0]?.date;
  const lastChartDate = chartData[chartData.length - 1]?.date;
  const priceLabel = preferAdjustedClose ? 'Adjusted close' : 'Close';
  const referenceTimestamp = parseDateMs(referenceDate);
  const notableDateMarkers = useMemo(() => (
    (Array.isArray(notableDates) ? notableDates : [])
      .map(normalizeNotableDateMarker)
      .filter(Boolean)
      .toSorted((a, b) => a.timestamp - b.timestamp)
  ), [notableDates]);
  const visibleNotableDateMarkers = useMemo(() => (
    notableDateMarkers.filter((marker) => !hiddenNotableDateKeys.has(marker.key))
  ), [hiddenNotableDateKeys, notableDateMarkers]);
  // Keep all in-range markers mounted; hide/show toggles visibility without remounting the price line.
  const chartNotableDateMarkers = useMemo(() => {
    if (!chartData.length) {
      return [];
    }

    const priceTimestamps = chartData.map((price) => price.dateMs);
    const loadedRangeStart = Math.min(...priceTimestamps);
    const loadedRangeEnd = Math.max(...priceTimestamps);
    return notableDateMarkers.filter(
      (marker) => marker.timestamp >= loadedRangeStart && marker.timestamp <= loadedRangeEnd,
    );
  }, [chartData, notableDateMarkers]);
  const allNotableDatesHidden = notableDateMarkers.length > 0
    && visibleNotableDateMarkers.length === 0;
  const toggleNotableDate = useCallback((key) => {
    setHiddenNotableDateKeys((current) => {
      const next = new Set(current);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
    setHoveredNotableDateKey(null);
  }, []);
  const toggleAllNotableDates = useCallback(() => {
    setHiddenNotableDateKeys(
      allNotableDatesHidden
        ? new Set()
        : new Set(notableDateMarkers.map((marker) => marker.key)),
    );
    setHoveredNotableDateKey(null);
  }, [allNotableDatesHidden, notableDateMarkers]);
  const handleRemoveNotableDate = useCallback(async (marker) => {
    if (!onRemoveNotableDate || !marker?.id || removingNotableDateKey) {
      return;
    }

    setRemovingNotableDateKey(marker.key);
    setHoveredNotableDateKey(null);
    try {
      await onRemoveNotableDate(marker);
      setHiddenNotableDateKeys((current) => {
        if (!current.has(marker.key)) return current;
        const next = new Set(current);
        next.delete(marker.key);
        return next;
      });
    } finally {
      setRemovingNotableDateKey(null);
    }
  }, [onRemoveNotableDate, removingNotableDateKey]);
  const xDomain = useMemo(() => {
    const timestamps = chartData.map((price) => price.dateMs);
    if (referenceTimestamp !== null) {
      timestamps.push(referenceTimestamp);
    }

    if (!timestamps.length) {
      return ['dataMin', 'dataMax'];
    }

    return [Math.min(...timestamps), Math.max(...timestamps)];
  }, [chartData, referenceTimestamp]);

  const tooltipRenderer = useCallback(
    ({ active, payload }) => {
      if (!active || !payload?.length) return null;

      const point = payload[0]?.payload || {};
      const volume = formatVolume(point.volume);

      return (
        <Stack
          gap="1"
          bg={tooltipBg}
          border="1px solid"
          borderColor={tooltipBorder}
          borderRadius="md"
          boxShadow="lg"
          p="3"
          color={tooltipText}
        >
          <Text fontWeight="semibold">{point.date}</Text>
          {point.adj_close !== null && (
            <Box display="flex" justifyContent="space-between" gap="4">
              <Text fontSize="sm" color={tooltipMuted}>Adjusted close</Text>
              <Text fontSize="sm" fontWeight="semibold">{formatTooltipPrice(point.adj_close)}</Text>
            </Box>
          )}
          {point.close !== null && (
            <Box display="flex" justifyContent="space-between" gap="4">
              <Text fontSize="sm" color={tooltipMuted}>Closing price</Text>
              <Text fontSize="sm" fontWeight="semibold">{formatTooltipPrice(point.close)}</Text>
            </Box>
          )}
          {volume && (
            <Box display="flex" justifyContent="space-between" gap="4">
              <Text fontSize="sm" color={tooltipMuted}>Volume</Text>
              <Text fontSize="sm" fontWeight="semibold">{volume}</Text>
            </Box>
          )}
        </Stack>
      );
    },
    [tooltipBg, tooltipBorder, tooltipMuted, tooltipText],
  );

  if (loading) {
    return (
      <Box
        minH="320px"
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="md"
        bg={panelBg}
        display="flex"
        alignItems="center"
        justifyContent="center"
        data-testid="stock-price-history-loading"
      >
        <Spinner size="lg" />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert.Root status="error" borderRadius="md" data-testid="stock-price-history-error">
        <Alert.Indicator />
        <Alert.Content>{getErrorMessage(error)}</Alert.Content>
      </Alert.Root>
    );
  }

  if (!chartData.length) {
    return (
      <Box
        minH="220px"
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="md"
        bg={emptyBg}
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexDirection="column"
        gap="1"
        px="4"
        textAlign="center"
        data-testid="stock-price-history-empty"
      >
        <Text color={mutedText} fontSize="sm" fontWeight="medium">
          {statusCopy?.label || (redactionEnabled
            ? 'No pre-snapshot price history'
            : 'No stock price history')}
        </Text>
        <Text color={mutedText} fontSize="xs">
          {statusCopy?.detail || 'No usable prices are available for this range.'}
        </Text>
      </Box>
    );
  }

  return (
    <Box
      w="full"
      minH="320px"
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="md"
      bg={panelBg}
      p="3"
      data-testid="stock-price-history-chart"
    >
      <Stack
        direction={{ base: 'column', sm: 'row' }}
        justify="space-between"
        align={{ base: 'flex-start', sm: 'center' }}
        gap="1"
        mb="2"
      >
        <Text fontSize="sm" fontWeight="semibold" color={tooltipText}>
          {priceLabel}
        </Text>
        {firstChartDate && lastChartDate ? (
          <Text fontSize="xs" color={mutedText}>
            {firstChartDate} to {lastChartDate}
          </Text>
        ) : null}
      </Stack>
      {statusCopy ? (
        <Box
          borderWidth="1px"
          borderColor={statusCopy.tone === 'orange' ? 'orange.200' : borderColor}
          borderRadius="md"
          bg={statusCopy.tone === 'orange' ? 'orange.50' : emptyBg}
          px="3"
          py="2"
          mb="3"
          data-testid="stock-price-history-status"
        >
          <Text fontSize="xs" fontWeight="semibold" color={tooltipText}>
            {statusCopy.label}
          </Text>
          <Text fontSize="xs" color={mutedText}>
            {statusCopy.detail}
          </Text>
        </Box>
      ) : null}
      <Suspense
        fallback={
          <Box h={{ base: '260px', md: '320px' }} display="flex" alignItems="center" justifyContent="center">
            <Spinner size="lg" />
          </Box>
        }
      >
        <Box h={{ base: '260px', md: '320px' }}>
          <StockPriceHistoryRechartsShell
            chartData={chartData}
            gridStroke={gridStroke}
            axisColor={axisColor}
            formatPriceTick={formatPriceTick}
            formatDateTick={formatDateTick}
            tooltipRenderer={tooltipRenderer}
            referenceDate={referenceDate}
            referenceLabel={referenceLabel}
            referenceTimestamp={referenceTimestamp}
            notableDateMarkers={chartNotableDateMarkers}
            hiddenNotableDateKeys={hiddenNotableDateKeys}
            hoveredNotableDateKey={hoveredNotableDateKey}
            onNotableDateHover={setHoveredNotableDateKey}
            xDomain={xDomain}
          />
        </Box>
      </Suspense>
      {notableDateMarkers.length > 0 ? (
        <Stack gap="2" mt="4" data-testid="notable-date-list">
          <Box display="flex" alignItems="center" justifyContent="space-between" gap="3">
            <Box>
              <Text fontSize="sm" fontWeight="bold" color={tooltipText}>Notable dates</Text>
              <Text fontSize="xs" color={mutedText}>Hover an event to highlight it on the chart.</Text>
            </Box>
            <Button
              type="button"
              size="xs"
              variant="ghost"
              colorPalette="purple"
              onClick={toggleAllNotableDates}
              aria-label={allNotableDatesHidden ? 'Show all notable dates' : 'Hide all notable dates'}
            >
              {allNotableDatesHidden ? <LuEye /> : <LuEyeOff />}
              {allNotableDatesHidden ? 'Show all' : 'Hide all'}
            </Button>
          </Box>
          {notableDateMarkers.map((marker) => (
            <Box
              key={marker.key}
              display="grid"
              gridTemplateColumns="auto minmax(0, 1fr) auto"
              alignItems="start"
              gap="2"
              px="2"
              py="2"
              borderRadius="md"
              bg={hoveredNotableDateKey === marker.key ? notableHoverBg : 'transparent'}
              opacity={hiddenNotableDateKeys.has(marker.key) ? 0.58 : 1}
              transition="background-color 120ms ease, opacity 120ms ease"
              fontSize="sm"
              color={tooltipText}
              tabIndex={0}
              data-testid={`notable-date-${marker.key}`}
              onMouseEnter={() => {
                if (!hiddenNotableDateKeys.has(marker.key)) setHoveredNotableDateKey(marker.key);
              }}
              onMouseLeave={() => setHoveredNotableDateKey(null)}
              onFocus={() => {
                if (!hiddenNotableDateKeys.has(marker.key)) setHoveredNotableDateKey(marker.key);
              }}
              onBlur={() => setHoveredNotableDateKey(null)}
              _hover={{ bg: hiddenNotableDateKeys.has(marker.key) ? notableMutedBg : notableHoverBg }}
              _focusVisible={{ outline: '2px solid', outlineColor: 'purple.400' }}
            >
              <Box display="flex" alignItems="center" gap="2" minW="7rem" pt="0.5">
                <Box
                  w="8px"
                  h="8px"
                  borderRadius="full"
                  bg={hiddenNotableDateKeys.has(marker.key) ? 'gray.400' : '#805AD5'}
                  flexShrink={0}
                />
                <Text as="span" color={mutedText} whiteSpace="nowrap">
                  {marker.event_date}
                </Text>
              </Box>
              {marker.filing_url ? (
                <Link
                  href={marker.filing_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  color={hiddenNotableDateKeys.has(marker.key) ? mutedText : 'blue.600'}
                  fontWeight="medium"
                  lineHeight="1.45"
                >
                  {marker.description}
                </Link>
              ) : (
                <Text as="span" fontWeight="medium" lineHeight="1.45">{marker.description}</Text>
              )}
              <Box display="flex" alignItems="center" gap="0.5" pt="0.5">
                <UiTooltip
                  content={hiddenNotableDateKeys.has(marker.key) ? 'Show on chart' : 'Hide from chart'}
                  positioning={{ placement: 'left' }}
                >
                  <IconButton
                    type="button"
                    size="xs"
                    variant="ghost"
                    colorPalette="purple"
                    aria-label={hiddenNotableDateKeys.has(marker.key)
                      ? `Show notable date ${marker.event_date}`
                      : `Hide notable date ${marker.event_date}`}
                    onClick={() => toggleNotableDate(marker.key)}
                  >
                    {hiddenNotableDateKeys.has(marker.key) ? <LuEyeOff /> : <LuEye />}
                  </IconButton>
                </UiTooltip>
                {onRemoveNotableDate ? (
                  <UiTooltip
                    content="Remove from timeline"
                    positioning={{ placement: 'left' }}
                  >
                    <IconButton
                      type="button"
                      size="xs"
                      variant="ghost"
                      colorPalette="red"
                      aria-label={`Remove notable date ${marker.event_date} from timeline`}
                      disabled={removingNotableDateKey === marker.key}
                      onClick={() => handleRemoveNotableDate(marker)}
                    >
                      <LuX />
                    </IconButton>
                  </UiTooltip>
                ) : null}
              </Box>
            </Box>
          ))}
        </Stack>
      ) : null}
    </Box>
  );
}
